'use client';
import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useT } from '../../lib/i18n';
import type { Classroom, ClassroomLecture } from '../../lib/myclass-shared';
import { useLayerLock } from '../../lib/useLayerLock';
import { Icon } from '../Icon';
import { Working } from '../Working';
import { toast } from '../Toast';

const ICON_CLOSE = 'x';
const ICON_SEND = 'send';

/**
 * 질문하기 — **누르기 전에 결정할 것이 없다**(2026-09-10 사용자 확정).
 *
 * 현행은 강사별 게시판(`/board/teacher/teacher_qna/{tid}`)을 학습자에게 그대로 내보였다.
 * 강사가 몇 명인지·어느 게시판인지는 **운영 구조**일 뿐 학습자의 관심사가 아니다.
 * 학습자는 "3강 이 부분이 이해가 안 돼" 라고 생각할 뿐이다.
 *
 * 그래서 규칙은 하나다 — **결정은 시스템이 하고, 학습자는 바꿀 수만 있다.**
 *  · 강좌·차시는 **최근 본 것**으로 채워 둔다(대부분 그대로 쓴다)
 *  · 받는 분은 고른 강좌의 **첫 강사**로 자동. 여럿이면 셀렉트로 바뀌지만 **안 바꿔도 된다**
 *  · 질문 받을 강사가 없으면(미지정·Q&A 없음) **운영자(1:1 문의)로 자동으로 물러난다**.
 *    버튼을 없애면 질문할 곳이 없는 강좌가 생긴다 — 학습자에게는 막힌 문이다
 *  · 제목도 미리 채운다. 학습자가 쓸 것은 **내용 하나**다
 *
 * 저장은 게시판과 같은 창구(`POST /boards/posts`)다 — `productId`·`teacherId` 를 함께 보내
 * 관리자·강사가 어느 강좌 이야기인지 안다. 문의 갈래는 서버가 게시판 종류로 판정한다.
 */
export function AskModal({ data, lecture, onClose }: {
  data: Classroom;
  /** 강사 줄에서 열었으면 그 강좌 — 비우면 최근 본 강좌부터 시작한다 */
  lecture?: ClassroomLecture;
  onClose: () => void;
}) {
  const t = useT();
  useLayerLock(true);

  /*
   * 처음 고를 강좌 — **지금 펼쳐 둔 강좌**, 없으면 맨 위(2026-09-10 사용자 확정).
   * 펼쳐 놓고 보던 강좌에 대한 질문일 확률이 가장 높다.
   */
  const [pid, setPid] = useState<number>(lecture?.productId ?? data.lectures[0]?.productId ?? 0);
  const picked = data.lectures.find((l) => l.productId === pid) ?? data.lectures[0];

  /**
   * 그 강좌에서 처음 고를 차시 — **최근 수강 표시가 붙은 차시**, 없으면 맨 위.
   * 차시를 안 고른 채로 두지 않는다(사용자 확정) — 대개 특정 차시를 두고 묻는다.
   */
  const firstCcid = (l: ClassroomLecture | undefined) => {
    if (!l || l.curriculum.length === 0) return '' as const;
    let best: { ccid: number; at: number } | null = null;
    for (const c of l.curriculum) {
      if (!c.lastStudiedAt) continue;
      const at = new Date(c.lastStudiedAt).getTime();
      if (!best || at > best.at) best = { ccid: c.ccid, at };
    }
    return best?.ccid ?? l.curriculum[0].ccid;
  };
  const [ccid, setCcid] = useState<number | ''>(() => firstCcid(picked));

  /*
   * 받는 분 후보 — 게시판이 있는 강사만. 하나도 없으면 운영자(1:1 문의)로 물러난다.
   *
   * **둘 이상이면 아무도 고르지 않은 채로 시작한다**(2026-09-10 사용자 확정) — 누구에게 갈지를
   * 시스템이 임의로 정하면 회원이 모르고 엉뚱한 강사에게 보낸다. 하나뿐이면 고를 것이 없으므로
   * 그대로 정해진다.
   */
  const askable = (picked?.teachers ?? []).filter((x) => x.qnaBoardId != null);
  const [teacherId, setTeacherId] = useState<number | ''>('');
  const to = askable.find((x) => x.id === teacherId) ?? (askable.length === 1 ? askable[0] : null);
  /** 골라야 하는데 아직 안 골랐다 — 보낼 때 막고 까닭을 말한다 */
  const needPick = askable.length > 1 && to == null;
  /*
   * 갈 곳 — 고른 강사의 게시판. 후보가 하나도 없을 때만 1:1 문의로 물러난다.
   * **안 고른 것과 없는 것을 가른다** — 안 골랐는데 운영자에게 보내면 회원의 뜻과 다르다.
   */
  const boardId = to ? to.qnaBoardId : (askable.length === 0 ? data.helpBoardId : null);

  const lessonNo = (n: number | null) => (n != null ? `${n}. ` : '');
  const [subject, setSubject] = useState('');
  const [content, setContent] = useState('');
  const [busy, setBusy] = useState(false);

  /** 강좌를 바꾸면 차시·받는 분은 새 강좌 기준으로 다시 정해진다(옛 선택은 뜻을 잃는다) */
  const changeLecture = (next: number) => {
    setPid(next);
    setCcid(firstCcid(data.lectures.find((l) => l.productId === next)));
    setTeacherId('');
  };

  const send = async () => {
    // 버튼을 잠그지 않고 까닭을 말한다(무반응 금지 규칙)
    if (needPick) { toast(t('mc.ask.pickTeacher'), 'error'); return; }
    if (!boardId) { toast(t('mc.ask.noBoard'), 'error'); return; }
    if (!content.trim()) { toast(t('mc.ask.needContent'), 'error'); return; }
    setBusy(true);
    try {
      const lesson = picked?.curriculum.find((c) => c.ccid === ccid);
      // 제목을 비워 두면 차시·강좌 이름으로 짓는다 — 학습자가 쓸 것은 내용 하나다
      const title = subject.trim()
        || (lesson ? t('mc.ask.autoLesson', { n: lessonNo(lesson.cnum) + lesson.cname }) : t('mc.ask.autoCourse', { n: picked?.name ?? '' }));
      const r = await fetch('/api/boards/posts', {
        method: 'POST', headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          settingId: boardId, subject: title, content: content.trim(),
          productId: pid, teacherId: to?.id, isSecret: true,
        }),
      });
      if (!r.ok) {
        const b = (await r.json().catch(() => ({}))) as { message?: string };
        toast(b.message ?? t('mc.mg.failed'), 'error');
        return;
      }
      toast(t('mc.ask.sent'));
      onClose();
    } catch {
      toast(t('acc.err.network'), 'error');
    } finally { setBusy(false); }
  };

  /* 단과는 구성 강좌가 자기 하나라 고를 것이 없다 — 셀렉트 대신 이름만 적는다 */
  const single = data.lectures.length <= 1;

  return createPortal(
    <>
      <div className="modal-backdrop open"></div>
      <div className="modal modal-lg open" role="dialog" aria-modal="true">
        <div className="modal-header">
          <h3 className="modal-title">{t('mc.ask.title')}</h3>
          <button className="modal-close" onClick={onClose} aria-label={t('action.close')}><Icon name={ICON_CLOSE} /></button>
        </div>
        <div className="modal-body">
          <div className="mgm-info-table">
            <div className="mgm-info-row">
              <span className="mgm-info-label">{t('mc.ask.course')}</span>
              <div className="mgm-info-val">
                {single ? (picked?.name ?? '-') : (
                  <select className="form-select ask-select" value={pid} onChange={(e) => changeLecture(Number(e.target.value))}>
                    {data.lectures.map((l) => <option key={l.productId} value={l.productId}>{l.name}</option>)}
                  </select>
                )}
              </div>
            </div>
            <div className="mgm-info-row">
              <span className="mgm-info-label">{t('mc.ask.lesson')}</span>
              <div className="mgm-info-val">
                <select className="form-select ask-select" value={ccid} onChange={(e) => setCcid(e.target.value ? Number(e.target.value) : '')}>
                  <option value="">{t('mc.ask.lessonNone')}</option>
                  {(picked?.curriculum ?? []).map((c) => (
                    <option key={c.ccid} value={c.ccid}>{lessonNo(c.cnum)}{c.cname}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="mgm-info-row">
              <span className="mgm-info-label">{t('mc.ask.to')}</span>
              <div className="mgm-info-val">
                {/*
                  강사가 하나뿐이거나 없으면 **고를 것이 없다** — 누구에게 가는지만 알린다.
                  여럿일 때만 셀렉트가 되고, 그때도 기본값이 이미 골라져 있다.
                */}
                {askable.length > 1 ? (
                  <select className="form-select ask-select" value={to?.id ?? ''}
                    onChange={(e) => setTeacherId(e.target.value ? Number(e.target.value) : '')}>
                    {/* 아무도 고르지 않은 채로 시작한다 — 시스템이 임의로 정하지 않는다 */}
                    <option value="">{t('mc.ask.pickTeacherPh')}</option>
                    {askable.map((x) => <option key={x.id} value={x.id}>{t('mc.ask.teacher', { n: x.name ?? '' })}</option>)}
                  </select>
                ) : (
                  <span>{to ? t('mc.ask.teacher', { n: to.name ?? '' }) : t('mc.ask.staff')}</span>
                )}
              </div>
            </div>
          </div>

          <input type="text" className="form-input mt15 mb10" value={subject} maxLength={100}
            placeholder={t('mc.ask.subjectPh')} onChange={(e) => setSubject(e.target.value)} />
          <textarea className="form-textarea" rows={7} value={content} maxLength={4000}
            placeholder={t('mc.ask.contentPh')} onChange={(e) => setContent(e.target.value)} />
          {/* 받는 분이 운영자로 물러났으면 그 사실을 말한다 — 강사에게 간 줄 알면 안 된다 */}
          {!to && <p className="form-hint mt10">{t('mc.ask.staffHint')}</p>}

          {/*
            버튼은 **본문 안 오른쪽 아래**다 — 가이드에 `modal-footer` 가 없다.
            주 액션이 오른쪽, 취소가 왼쪽(하단 버튼 위치 규칙).
          */}
          <div className="d-flex jc-between mt20">
            <button className="action-btn action-cancel" onClick={onClose}>{t('action.cancel')}</button>
            <button className="action-btn action-submit" onClick={() => void send()} disabled={busy}>
              {busy ? <Working inline /> : <Icon name={ICON_SEND} />} {t('mc.ask.send')}
            </button>
          </div>
        </div>
      </div>
    </>,
    document.body,
  );
}
